import { useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiService } from '../../../shared/services/api'
import { useAuth } from '../../../shared/components/ProtectedRoute'
import UserHeader from '../components/UserHeader'
import AnalyticsFilters from '../components/analytics/AnalyticsFilters'
import ClicksTimeSeries from '../components/analytics/ClicksTimeSeries'
import LinkPicker from '../components/analytics/LinkPicker'
import AnalyticsSummary from '../components/analytics/AnalyticsSummary'
import DeviceBreakdown from '../components/analytics/DeviceBreakdown'
import TopLinksList from '../components/analytics/TopLinksList'
import { useTranslation } from 'react-i18next'
import type { AnalyticsRange } from '../../../shared/types/analytics'

const RANGE_VALUES: AnalyticsRange[] = ['1d', '7d', '30d', '90d']
const REFETCH_INTERVAL_MS = 300_000

function parseRange(value: string | null): AnalyticsRange {
  return RANGE_VALUES.includes(value as AnalyticsRange)
    ? (value as AnalyticsRange)
    : '7d'
}

/** 계정 전체 분석 + 링크별 정밀 분석 진입점 */
export default function AnalyticsPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const range = parseRange(searchParams.get('range'))
  const includeBots = searchParams.get('includeBots') === 'true'
  const timeZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Seoul',
    []
  )

  const updateParams = useCallback(
    (next: Record<string, string | null>) => {
      const params = new URLSearchParams(searchParams)
      Object.entries(next).forEach(([key, value]) => {
        if (value === null) {
          params.delete(key)
        } else {
          params.set(key, value)
        }
      })
      setSearchParams(params, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  const { data, isLoading, isError } = useQuery({
    queryKey: ['analytics', 'overview', range, includeBots, timeZone],
    queryFn: () =>
      apiService.getAnalyticsOverview({ range, tz: timeZone, includeBots }),
    enabled: !!user,
    refetchInterval: REFETCH_INTERVAL_MS,
  })

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <UserHeader
        title={t('analyticsPage.title')}
        subtitle={t('analyticsPage.subtitle', {
          name: user?.name || user?.username || '',
        })}
      />

      <div className="container mx-auto space-y-6 px-6 py-8">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <AnalyticsFilters
            range={range}
            onRangeChange={value => updateParams({ range: value })}
            includeBots={includeBots}
            onIncludeBotsChange={value =>
              updateParams({ includeBots: value ? 'true' : null })
            }
          />
          <div className="w-full lg:w-96">
            <LinkPicker
              onSelect={code =>
                navigate(`/analytics/${code}?${searchParams.toString()}`)
              }
              placeholder={t('analyticsPage.openPicker')}
            />
          </div>
        </div>

        {isError && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
            {t('analyticsPage.overviewError')}
          </div>
        )}

        {isLoading || !data ? (
          <div className="animate-pulse space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              {[...Array(4)].map((_, index) => (
                <div
                  key={index}
                  className="h-32 rounded-2xl bg-gray-200 dark:bg-gray-700"
                />
              ))}
            </div>
            <div className="h-80 rounded-2xl bg-gray-200 dark:bg-gray-700" />
          </div>
        ) : (
          <>
            <AnalyticsSummary
              cells={[
                {
                  label: t('analyticsPage.periodClicks'),
                  value: data.overview.totalClicks.toLocaleString(),
                  change: data.overview.clicksChange,
                  hint: t('analyticsPage.vsPrevious'),
                },
                {
                  label: t('analyticsPage.uniqueVisitors'),
                  value: data.overview.uniqueClicks.toLocaleString(),
                  change: data.overview.uniqueClicksChange,
                  hint: t('analyticsPage.vsPrevious'),
                },
                {
                  label: t('analyticsPage.newLinks'),
                  value: data.overview.urlsCreatedInPeriod.toLocaleString(),
                  change: data.overview.urlsChange,
                  hint: t('analyticsPage.totalLinks', {
                    count: data.overview.totalUrls.toLocaleString(),
                  }),
                },
              ]}
            />

            <ClicksTimeSeries
              data={data.clicksOverTime}
              granularity={range === '1d' ? 'hour' : 'day'}
            />

            {/* 기기 분포는 좁게, 상위 링크는 넓게. 목록이 가로로 넓어야 읽힌다 */}
            <div className="flex flex-wrap items-start gap-5">
              <div className="min-w-0 flex-[1_1_320px]">
                <DeviceBreakdown stats={data.deviceStats} />
              </div>
              <div className="min-w-0 flex-[2_1_520px]">
                <TopLinksList
                  links={data.topUrls}
                  queryString={searchParams.toString()}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
